import React, { useState, useEffect } from 'react';
import { useData } from "../backend/firebase";
// import { db } from "../backend/firebase";
// import { doc, setDoc, onSnapshot, collection } from "firebase/firestore";
import { DateTime } from 'luxon';
import { getImagePath } from '../utils/imageRegistry';

const CaptainPortal = () => {
    const { user, userRole, teams, schedule, toggleCommentary, allLineups, saveMatchLineup, updateEvent } = useData();
    const [adminSelectedTeam, setAdminSelectedTeam] = useState('ALL');
    
    // NEW: Track unsaved changes locally before sending to Firebase
    const [localEdits, setLocalEdits] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const [showOnlyKO, setShowOnlyKO] = useState(false);

    const isAdmin = userRole?.role === 'admin';
    const captainTeamName = userRole?.teamName || teams.find(t => t.id === userRole?.teamId)?.name;
    const activeTeamName = isAdmin ? adminSelectedTeam : captainTeamName;

    const activeTeamObj = teams.find(t => t.name === activeTeamName);
    const activeTeamId = activeTeamObj ? activeTeamObj.id : null;

    // NEW: Drop admin privileges when impersonating a specific team
    const isEffectiveAdmin = isAdmin && adminSelectedTeam === 'ALL';

    // --- UNIFIED PERSPECTIVES STATE & LOGIC ---
    const [localMatchLinks, setLocalMatchLinks] = useState({});

    const getMatchLinks = (matchId) => {
        if (localMatchLinks[matchId]) return localMatchLinks[matchId];
        const match = schedule.find(m => m.id === matchId);
        return match?.links || [];
    };

    const handleUpdateMatchLink = (matchId, index, field, value) => {
        const current = [...getMatchLinks(matchId)];
        current[index] = { ...current[index], [field]: value };
        setLocalMatchLinks(prev => ({ ...prev, [matchId]: current }));
    };

    const handleAddMatchLink = (matchId, myTeamId) => {
        const current = [...getMatchLinks(matchId)];
        
        // Check if this team already has a primary perspective (teamId === myTeamId)
        const hasPrimary = current.some(l => l.teamId === myTeamId);
        
        // Automatically assign the primary teamId if this is their first link
        current.push({ 
            url: '', 
            label: '', 
            teamId: hasPrimary ? '' : (myTeamId || ''), 
            associatedTeamId: myTeamId || '' 
        });
        
        setLocalMatchLinks(prev => ({ ...prev, [matchId]: current }));
    };

    const handleRemoveMatchLink = (matchId, index) => {
        const current = [...getMatchLinks(matchId)];
        current.splice(index, 1);
        setLocalMatchLinks(prev => ({ ...prev, [matchId]: current }));
    };

    // Admin Only Reordering
    const moveMatchLinkUp = (matchId, index) => {
        if (index === 0) return;
        const current = [...getMatchLinks(matchId)];
        [current[index], current[index - 1]] = [current[index - 1], current[index]];
        setLocalMatchLinks(prev => ({ ...prev, [matchId]: current }));
    };

    const moveMatchLinkDown = (matchId, index) => {
        const current = [...getMatchLinks(matchId)];
        if (index === current.length - 1) return;
        [current[index], current[index + 1]] = [current[index + 1], current[index]];
        setLocalMatchLinks(prev => ({ ...prev, [matchId]: current }));
    };

    const saveMatchLinks = async (matchId) => {
        if (!localMatchLinks[matchId]) return;
        setIsSaving(true);
        try {
            // Clean out entirely empty rows before saving
            const cleanLinks = localMatchLinks[matchId].filter(l => l.url.trim() !== '' || l.label.trim() !== '');
            await updateEvent(matchId, { links: cleanLinks });
            
            // Clear local state to sync back with Firebase
            setLocalMatchLinks(prev => {
                const newState = { ...prev };
                delete newState[matchId];
                return newState;
            });
        } catch (e) {
            console.error("Error saving links:", e);
            alert("Failed to save stream perspectives.");
        }
        setIsSaving(false);
    };

    const resolveTeam = (identifier) => {
        if (!identifier) return { name: "TBA", color: '#555', obj: null };
        const strId = identifier.toString();
        const found = teams.find(t => t.id?.toString() === strId || t.name === identifier);
        return found ? { name: found.name, color: found.color || '#555', obj: found } : { name: identifier, color: '#555', obj: null };
    };

    const TeamIcon = ({ team, size = 32 }) => {
        if (!team) return <div style={{width: size, height: size, background: '#333', borderRadius: '50%'}} />;
        if (team.logoUrl) return <img src={getImagePath(team.logoUrl)} alt={team.name} style={{width: size, height: size, objectFit: 'contain'}} />;
        if (team.logo) return <img src={`data:image/svg+xml;utf8,${encodeURIComponent(team.logo)}`} alt={team.name} style={{width: size, height: size, objectFit: 'contain'}} />;
        return <div style={{width: size, height: size, background: team.color || '#333', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)'}} />;
    };

    // NEW: Handle typing without saving to database
    const handleLocalChange = (matchId, position, value) => {
        setLocalEdits(prev => ({
            ...prev,
            [matchId]: {
                ...(prev[matchId] || {}),
                [position]: value
            }
        }));
    };

    // NEW: The Submit Function
    const submitLineup = async (matchId, teamKey) => {
        const matchEdits = localEdits[matchId];
        if (!matchEdits) return; // Nothing to save

        setIsSaving(true);
        const currentDbData = allLineups[matchId]?.[teamKey] || {};
        const mergedData = { ...currentDbData, ...matchEdits, isFinalized: false };

        try {
            await saveMatchLineup(matchId, teamKey, mergedData);
            
            // Clear local edits for this match so it falls back to showing DB state
            setLocalEdits(prev => {
                const newState = { ...prev };
                delete newState[matchId];
                return newState;
            });
        } catch (error) {
            console.error("Error saving lineup:", error);
            alert("Failed to save lineup. Check your permissions.");
        }
        setIsSaving(false);
    };

    const handleFinalize = async (matchId, teamKey) => {
        setIsSaving(true);
        const currentDbData = allLineups[matchId]?.[teamKey] || {};
        try {
            await saveMatchLineup(matchId, teamKey, { ...currentDbData, isFinalized: true });
        } catch (error) {
            console.error("Error finalizing lineup:", error);
            alert("Failed to finalize lineup.");
        }
        setIsSaving(false);
    };

    const handleUnlockLineups = async (matchId) => {
        if (!window.confirm("Unlock lineups for both teams? This will allow captains to edit them again.")) return;
        setIsSaving(true);
        const currentData = allLineups[matchId] || {};
        const t1 = currentData.team1Lineup || {};
        const t2 = currentData.team2Lineup || {};
        try {
            await saveMatchLineup(matchId, 'team1Lineup', { ...t1, isFinalized: false });
            await saveMatchLineup(matchId, 'team2Lineup', { ...t2, isFinalized: false });
        } catch (e) {
            console.error("Error unlocking lineups:", e);
            alert("Failed to unlock lineups.");
        }
        setIsSaving(false);
    };

    if (userRole?.role !== 'captain' && userRole?.role !== 'admin') {
        return <div className="PageLayout"><div className="ContentWrapper"><h1 style={{color: 'white'}}>Access Denied</h1></div></div>;
    }

    if (!isAdmin && !activeTeamName) {
        return <div className="PageLayout"><div className="ContentWrapper"><h1 style={{color: 'white'}}>No team assigned. Contact Admin.</h1></div></div>;
    }

    const processedMatches = schedule.map(match => ({
        ...match,
        t1Data: resolveTeam(match.team1),
        t2Data: resolveTeam(match.team2)
    }));

    let displayedMatches = activeTeamName === 'ALL' 
        ? processedMatches 
        : processedMatches.filter(m => m.t1Data.name === activeTeamName || m.t2Data.name === activeTeamName);

    if (showOnlyKO) {
        displayedMatches = displayedMatches.filter(m => m.stage === 'KO' || m.stage === 'Final');
    }

    const renderPositionInputs = (match, teamName, teamKey) => {
        const currentDbData = allLineups[match.id]?.[teamKey] || {};
        const currentLocalData = localEdits[match.id] || {};
        const positions = ['North', 'South', 'East', 'West'];

        // Knockout Logic
        const isKnockout = match.stage === 'KO' || match.stage === 'Final';
        const myTeamFinalized = currentDbData.isFinalized || false;
        const oppTeamKey = teamKey === 'team1Lineup' ? 'team2Lineup' : 'team1Lineup';
        const oppTeamFinalized = allLineups[match.id]?.[oppTeamKey]?.isFinalized || false;
        const bothFinalized = isKnockout && myTeamFinalized && oppTeamFinalized;
        
        // Check if there are unsaved changes for this specific match
        const hasUnsavedChanges = Object.keys(currentLocalData).length > 0;

        return (
            <div style={{ marginTop: 15, padding: '0 25px 25px 25px' }}>
                <div style={{ fontSize: 13, color: '#FFD700', marginBottom: 12, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Editing Lineup: {teamName}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                    {positions.map(pos => {
                        const posKey = pos.toLowerCase();
                        // If there is a local edit, show it. Otherwise, show what's in the DB.
                        const displayValue = currentLocalData[posKey] !== undefined 
                            ? currentLocalData[posKey] 
                            : (currentDbData[posKey] || "");

                        return (
                            <div key={posKey} style={{ display: 'flex', flexDirection: 'column' }}>
                                <label style={{ fontSize: 11, color: '#8F9EB5', marginBottom: 4, textTransform: 'uppercase', fontWeight: 'bold' }}>
                                    {pos} Player
                                </label>
                                <input 
                                    className="DarkInput"
                                    style={{ margin: 0, padding: '10px 12px', opacity: bothFinalized ? 0.5 : 1, cursor: bothFinalized ? 'not-allowed' : 'text' }}
                                    placeholder="Username..."
                                    maxLength={12} // NEW: Max length of 12 characters
                                    value={displayValue}
                                    onChange={(e) => handleLocalChange(match.id, posKey, e.target.value)}
                                    disabled={bothFinalized}
                                />
                            </div>
                        );
                    })}
                </div>
                
                {/* NEW: Submit Button */}
                {isKnockout && !bothFinalized && myTeamFinalized && (
                    <div style={{ marginTop: 15, padding: '10px', background: 'rgba(74, 222, 128, 0.1)', border: '1px solid rgba(74, 222, 128, 0.3)', borderRadius: 8, color: '#4ADE80', fontSize: 12, textAlign: 'center' }}>
                        Lineup finalized! You can still edit until the opponent also finalizes. Editing will un-finalize your lineup.
                    </div>
                )}
                {isKnockout && bothFinalized && (
                    <div style={{ marginTop: 15, padding: '10px', background: 'rgba(229, 62, 62, 0.1)', border: '1px solid rgba(229, 62, 62, 0.3)', borderRadius: 8, color: '#E53E3E', fontSize: 12, textAlign: 'center', fontWeight: 'bold' }}>
                        🔒 Both teams have finalized. Lineups are now locked and visible.
                    </div>
                )}

                {/* NEW: Submit & Finalize Buttons */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 15, gap: 10 }}>
                    {isKnockout && !bothFinalized && (
                        <button 
                            className="PrimaryButton"
                            onClick={() => handleFinalize(match.id, teamKey)}
                            disabled={hasUnsavedChanges || isSaving || myTeamFinalized}
                            style={{ 
                                background: (hasUnsavedChanges || myTeamFinalized) ? 'rgba(255,255,255,0.1)' : '#4ADE80',
                                color: (hasUnsavedChanges || myTeamFinalized) ? '#8F9EB5' : '#000',
                                cursor: (hasUnsavedChanges || myTeamFinalized) ? 'default' : 'pointer',
                                transition: 'all 0.2s ease', fontWeight: 'bold'
                            }}
                            title={hasUnsavedChanges ? "Save changes before finalizing" : ""}
                        >
                            {isSaving ? "Processing..." : myTeamFinalized ? "Finalized ✅" : "Finalize Lineup"}
                        </button>
                    )}
                    <button 
                        className="PrimaryButton"
                        onClick={() => submitLineup(match.id, teamKey)}
                        disabled={!hasUnsavedChanges || isSaving || bothFinalized}
                        style={{ 
                            background: hasUnsavedChanges && !bothFinalized ? '#6C5DD3' : 'rgba(255,255,255,0.1)',
                            color: hasUnsavedChanges && !bothFinalized ? '#fff' : '#8F9EB5',
                            cursor: hasUnsavedChanges && !bothFinalized ? 'pointer' : 'default',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {isSaving ? "Saving..." : hasUnsavedChanges ? "Save Lineup" : "Saved"}
                    </button>
                </div>
            </div>
        );
    };

    const renderReadOnlyLineup = (match, t1Name, t2Name) => {
        const t1Data = allLineups[match.id]?.team1Lineup || {};
        const t2Data = allLineups[match.id]?.team2Lineup || {};
        const positions = ['north', 'south', 'east', 'west'];

        const isKnockout = match.stage === 'KO' || match.stage === 'Final';
        const bothFinalized = isKnockout && t1Data.isFinalized && t2Data.isFinalized;

        // Helper to copy text and temporarily show a checkmark
        const handleCopy = (e, text) => {
            e.stopPropagation();
            navigator.clipboard.writeText(text);
            const btn = e.currentTarget;
            const oldText = btn.innerHTML;
            btn.innerHTML = "✅ Copied";
            setTimeout(() => { btn.innerHTML = oldText; }, 1000);
        };

        const formatLineup = (data) => {
            if (typeof data === 'string') return <div style={{marginTop: 8}}>{data}</div>; 
            const hasData = positions.some(p => data[p]);
            if (!hasData) return <div style={{color: '#555', marginTop: 8}}>No lineup entered</div>;
            
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
                    {positions.map(p => {
                        if (!data[p]) return null;
                        return (
                            <div key={p} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.3)', padding: '6px 12px', borderRadius: 6 }}>
                                <span>
                                    <strong style={{color: '#8F9EB5', display: 'inline-block', width: 20}}>{p.charAt(0).toUpperCase()}</strong> 
                                    <span style={{color: '#E0E0E0'}}>{data[p]}</span>
                                </span>
                                <button 
                                    onClick={(e) => handleCopy(e, data[p])}
                                    style={{ 
                                        background: 'rgba(108, 93, 211, 0.15)', border: '1px solid rgba(108, 93, 211, 0.4)', 
                                        color: '#A395E9', borderRadius: 4, cursor: 'pointer', fontSize: 10, 
                                        padding: '4px 8px', transition: 'all 0.2s ease', fontWeight: 'bold' 
                                    }}
                                    title="Copy Username"
                                >
                                    📋 Copy
                                </button>
                            </div>
                        );
                    })}
                </div>
            );
        };

        return (
            <>
                {isAdmin && bothFinalized && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 15, marginBottom: 15 }}>
                        {(match.stage === 'KO' || match.stage === 'Final') && (
                            <button 
                                onClick={() => handleUnlockLineups(match.id)} 
                                disabled={isSaving} 
                                style={{ background: 'rgba(74, 222, 128, 0.15)', color: '#4ADE80', border: '1px solid rgba(74, 222, 128, 0.3)', padding: '6px 12px', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer', transition: '0.2s', fontSize: 12 }}
                            >
                                {isSaving ? "Processing..." : "🔓 Unlock Lineups"}
                            </button>
                        )}
                    </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, padding: '0 25px 25px 25px', fontSize: 13 }}>
                    <div style={{ background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)' }}>
                        <strong style={{color: '#fff', fontSize: 15}}>{t1Name}</strong>
                        {formatLineup(t1Data)}
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)' }}>
                        <strong style={{color: '#fff', fontSize: 15}}>{t2Name}</strong>
                        {formatLineup(t2Data)}
                    </div>
                </div>
            </>
        );
    };

    const handleUndoChanges = (matchId) => {
        if (window.confirm("Discard all unsaved changes to these links?")) {
            setLocalMatchLinks(prev => {
                const newState = { ...prev };
                delete newState[matchId];
                return newState;
            });
        }
    };

    const renderPerspectives = (match, teamObjId) => {
        const matchLinks = getMatchLinks(match.id);
        const hasUnsavedLinks = !!localMatchLinks[match.id];

        return (
            <div style={{ padding: '0 25px 25px 25px' }}>
                <div style={{ borderTop: '1px dashed rgba(255,255,255,0.1)', paddingTop: 15 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <div style={{ fontSize: 12, color: '#8F9EB5', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
                            Stream Perspectives
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            {hasUnsavedLinks && (
                                <button 
                                    onClick={() => handleUndoChanges(match.id)}
                                    style={{ background: 'transparent', border: '1px solid #555', color: '#aaa', padding: '6px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 'bold', transition: '0.2s' }}
                                >
                                    Undo Changes
                                </button>
                            )}
                            <button 
                                className="PrimaryButton"
                                onClick={() => saveMatchLinks(match.id)}
                                disabled={!hasUnsavedLinks || isSaving}
                                style={{ 
                                    background: hasUnsavedLinks ? '#6C5DD3' : 'rgba(255,255,255,0.1)',
                                    color: hasUnsavedLinks ? '#fff' : '#8F9EB5',
                                    cursor: hasUnsavedLinks ? 'pointer' : 'default',
                                    padding: '6px 16px', fontSize: 11, margin: 0, transition: '0.2s', fontWeight: 'bold'
                                }}
                            >
                                {isSaving ? "Saving..." : hasUnsavedLinks ? "Save Links" : "Saved"}
                            </button>
                        </div>
                    </div>

                    {matchLinks.map((linkObj, index) => {
                        // Edit rights: True Admin, Primary Owner, or Associated Owner
                        const safeTeamObjId = teamObjId?.toString();
                        const canEdit = isAdmin || 
                                        (linkObj.teamId && linkObj.teamId.toString() === safeTeamObjId) || 
                                        (linkObj.associatedTeamId && linkObj.associatedTeamId.toString() === safeTeamObjId);

                        return (
                            <div key={index} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, opacity: canEdit ? 1 : 0.5 }}>
                                
                                {/* Admin Arrows */}
                                {isAdmin && (
                                    <div style={{display: 'flex', flexDirection: 'column'}}>
                                        <button onClick={(e)=>{e.preventDefault(); moveMatchLinkUp(match.id, index)}} disabled={index===0} style={{cursor: index===0 ? 'default' : 'pointer', background:'transparent', border:'none', color: index===0 ? '#444' : 'white', padding: '0 4px', fontSize: 10}}>▲</button>
                                        <button onClick={(e)=>{e.preventDefault(); moveMatchLinkDown(match.id, index)}} disabled={index===matchLinks.length-1} style={{cursor: index===matchLinks.length-1 ? 'default' : 'pointer', background:'transparent', border:'none', color: index===matchLinks.length-1 ? '#444' : 'white', padding: '0 4px', fontSize: 10}}>▼</button>
                                    </div>
                                )}
                                
                                <input 
                                    className="DarkInput" 
                                    style={{ margin: 0, flex: 2, cursor: canEdit ? 'text' : 'not-allowed' }} 
                                    placeholder="YouTube/Twitch URL" 
                                    maxLength={200} 
                                    value={linkObj.url || ''} 
                                    onChange={(e) => handleUpdateMatchLink(match.id, index, 'url', e.target.value)}
                                    disabled={!canEdit}
                                />
                                
                                <input 
                                    className="DarkInput" 
                                    style={{ margin: 0, flex: 1, cursor: canEdit ? 'text' : 'not-allowed' }} 
                                    placeholder="Label" 
                                    maxLength={50} 
                                    value={linkObj.label || ''} 
                                    onChange={(e) => handleUpdateMatchLink(match.id, index, 'label', e.target.value)}
                                    disabled={!canEdit}
                                />

                                {/* Primary Team Dropdown (Restricted for Captains) */}
                                <select 
                                    className="DarkInput" 
                                    style={{ margin: 0, flex: 1, cursor: canEdit ? 'pointer' : 'not-allowed' }} 
                                    value={linkObj.teamId || ''} 
                                    onChange={(e) => handleUpdateMatchLink(match.id, index, 'teamId', e.target.value)}
                                    disabled={!canEdit}
                                >
                                    <option value="">Additional Perspective</option>
                                    {isAdmin ? (
                                        teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)
                                    ) : (
                                        <>
                                            {/* Edge case: Show current assignment if it somehow belongs to an opponent but the captain has association rights to edit it */}
                                            {linkObj.teamId && linkObj.teamId.toString() !== safeTeamObjId && (
                                                <option value={linkObj.teamId} disabled>
                                                    {teams.find(t => t.id?.toString() === linkObj.teamId.toString())?.name || "Other Team"}
                                                </option>
                                            )}
                                            
                                            {/* The captain's only valid choice besides blank */}
                                            {teamObjId && (
                                                <option value={teamObjId}>{teams.find(t => t.id === teamObjId)?.name}</option>
                                            )}
                                        </>
                                    )}
                                </select>

                                {/* Association Dropdown (Hidden from Captains completely) */}
                                {isAdmin && (
                                    <select 
                                        className="DarkInput" 
                                        style={{ margin: 0, flex: 1 }} 
                                        value={linkObj.associatedTeamId || ''} 
                                        onChange={(e) => handleUpdateMatchLink(match.id, index, 'associatedTeamId', e.target.value)}
                                    >
                                        <option value="">Assoc?</option>
                                        {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                    </select>
                                )}

                                <div style={{ width: 30, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                                    {canEdit && (
                                        <button 
                                            onClick={(e) => { 
                                                e.preventDefault(); 
                                                if (window.confirm("Remove this perspective? (You still need to click 'Save Links' to apply this deletion)")) {
                                                    handleRemoveMatchLink(match.id, index); 
                                                }
                                            }} 
                                            style={{ background: '#FF5C5C', color: 'white', border: 'none', borderRadius: 8, width: 30, height: 30, cursor: 'pointer' }}
                                            title="Remove Link"
                                        >✕</button>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingLeft: isAdmin ? 20 : 0 }}>
                        <button 
                            onClick={(e) => { e.preventDefault(); handleAddMatchLink(match.id, teamObjId); }} 
                            style={{ background: 'transparent', border: '1px dashed #6C5DD3', color: '#6C5DD3', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}
                        >
                            + Add Perspective
                        </button>
                        <div style={{ fontSize: 10, color: '#555', fontStyle: 'italic' }}>
                            * Leave a URL blank to remove it on save.
                        </div>
                    </div>

                </div>
            </div>
        );
    };

    return (
        <div className="PageLayout">
            <div className="ContentWrapper CustomScroll" style={{ overflowY: 'auto' }}>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 15, marginBottom: 30, flexShrink: 0 }}>
                    <h1 className="SectionTitle" style={{ margin: 0 }}>
                        {isAdmin && activeTeamName === 'ALL' ? "Master Lineups" : `Captain Portal: ${activeTeamName}`}
                    </h1>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: 15, flexWrap: 'wrap' }}>
                        <button 
                            onClick={() => setShowOnlyKO(!showOnlyKO)}
                            style={{ 
                                background: showOnlyKO ? 'rgba(108, 93, 211, 0.15)' : 'rgba(0,0,0,0.3)', 
                                color: showOnlyKO ? '#A395E9' : '#8F9EB5', 
                                border: `1px solid ${showOnlyKO ? 'rgba(108, 93, 211, 0.4)' : 'rgba(255,255,255,0.1)'}`, 
                                padding: '8px 15px', borderRadius: 12, cursor: 'pointer', fontWeight: 'bold', fontSize: 13, transition: '0.2s' 
                            }}
                        >
                            {showOnlyKO ? "🏆 KO Matches Only" : "📋 All Matches"}
                        </button>

                        {isAdmin && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(0,0,0,0.3)', padding: '8px 15px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)' }}>
                                <label style={{ color: '#8F9EB5', fontSize: 13, fontWeight: 'bold', textTransform: 'uppercase' }}>Impersonate:</label>
                                <select 
                                    className="DarkInput" 
                                    style={{ margin: 0, width: 'auto', minWidth: 200, border: 'none', background: 'transparent', padding: 5, color: '#FFD700', fontWeight: 'bold' }}
                                    value={adminSelectedTeam}
                                    onChange={(e) => setAdminSelectedTeam(e.target.value)}
                                >
                                    <option value="ALL">All Matches (View Only)</option>
                                    {teams.map(t => (
                                        <option key={t.id} value={t.name}>{t.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {activeTeamName !== 'ALL' && (
                    <p style={{color: '#8F9EB5', marginBottom: 30, flexShrink: 0}}>
                        Assign your players to North, South, East, and West for each match. Hit <strong>Save Lineup</strong> when done. Max 12 characters per player.
                    </p>
                )}
                
                {displayedMatches.length === 0 && <div style={{color: '#8F9EB5', textAlign: 'center', padding: 50, background: 'rgba(0,0,0,0.2)', borderRadius: 12, flexShrink: 0}}>No matches found.</div>}

                {displayedMatches.map(match => {
                    const { t1Data, t2Data } = match;
                    const isTeam1 = t1Data.name === activeTeamName;
                    const teamKey = isTeam1 ? "team1Lineup" : "team2Lineup";

                    let formattedTime = "TBA";
                    if (match.startTime) {
                        const localTime = DateTime.fromISO(match.startTime, { zone: 'America/Los_Angeles' }).toLocal();
                        if (localTime.isValid) formattedTime = localTime.toFormat('cccc, MMM d @ h:mm a');
                    }

                    return (
                        <div key={match.id} className="GlassCard" style={{ flexShrink: 0, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
                            <div style={{ 
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                                background: `linear-gradient(90deg, ${t1Data.color}33 0%, ${t2Data.color}33 100%)`,
                                borderLeft: `6px solid ${t1Data.color}`, borderRight: `6px solid ${t2Data.color}`,
                                padding: '15px 25px', borderBottom: '1px solid rgba(255,255,255,0.05)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '35%' }}>
                                    <TeamIcon team={t1Data.obj} />
                                    <strong style={{color: 'white', fontSize: 16}}>{t1Data.name}</strong>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '30%', textAlign: 'center' }}>
                                    <span style={{color: '#FFD700', fontSize: 14, fontWeight: 'bold'}}>{formattedTime}</span>
                                    <span style={{color: '#8F9EB5', fontSize: 10, textTransform: 'uppercase', marginTop: 2}}>Local Time</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '35%', justifyContent: 'flex-end' }}>
                                    <strong style={{color: 'white', fontSize: 16, textAlign: 'right'}}>{t2Data.name}</strong>
                                    <TeamIcon team={t2Data.obj} />
                                </div>
                            </div>
                            
                            {activeTeamName === 'ALL' 
                                ? renderReadOnlyLineup(match, t1Data.name, t2Data.name)
                                : renderPositionInputs(match, activeTeamName, teamKey)
                            }
                            {/* Render Perspectives for EVERY view, resolving the Team ID safely */}
                            {renderPerspectives(match, activeTeamName !== 'ALL' ? (isTeam1 ? t1Data.obj?.id : t2Data.obj?.id) : null)}
                            {activeTeamName === 'ALL' && (
                            <div style={{ padding: '0 25px 25px 25px', display: 'flex', justifyContent: 'flex-end' }}>
                                <div style={{ background: 'rgba(0,0,0,0.3)', padding: 15, borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)', width: '100%', maxWidth: 350 }}>
                                    <div style={{ fontSize: '12px', color: '#8F9EB5', marginBottom: '8px', fontWeight: 'bold' }}>
                                        COMMENTATORS ({match.commentators?.length || 0} / 2)
                                    </div>
                                    
                                    {match.commentators && match.commentators.length > 0 && (
                                        <div style={{ marginBottom: 10, fontSize: 13, color: '#FFD700' }}>
                                            {match.commentators.map((c, idx) => <div key={idx}>🎤 {c}</div>)}
                                        </div>
                                    )}

                                    <button 
                                        onClick={() => toggleCommentary(match.id, user?.email)}
                                        className="PrimaryButton"
                                        style={{ 
                                            background: match.commentators?.includes(user?.email) ? '#E53E3E' : '#26A65B',
                                            margin: 0, width: '100%',
                                            opacity: (match.commentators?.length >= 2 && !match.commentators?.includes(user?.email)) ? 0.5 : 1
                                        }}
                                        disabled={match.commentators?.length >= 2 && !match.commentators?.includes(user?.email)}
                                    >
                                        {match.commentators?.includes(user?.email) ? 'Leave Commentary' : 'Sign up to Commentate'}
                                    </button>
                                </div>
                            </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CaptainPortal;